// Single source of truth for week-boundary arithmetic in the web package.
//
// Every function anchors at UTC noon (`T12:00:00Z`) and uses `getUTC*`/`setUTC*`.
// This deliberately avoids local-midnight arithmetic and `getDay()`/`setDate()`,
// which shift by a day across DST transitions. See
// docs/solutions/runtime-errors/local-midnight-date-breaks-week-math-in-dst-timezones-2026-05-07.md
//
// Weeks run Monday–Sunday. A "Monday string" is a canonical `YYYY-MM-DD` date
// that falls on a Monday; it is the identity used for cache keys and the
// `?week=` URL param.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Intl.DateTimeFormat construction is comparatively expensive and these run on
// every render (forward-cap computation, PeriodBar label, live region), so they
// are built once at module scope and reused.
const CHICAGO_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Chicago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const RANGE_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});
const RANGE_FMT_WITH_YEAR = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** True if `s` is a real `YYYY-MM-DD` calendar date (rejects e.g. 2026-02-31). */
export function isIsoDate(s: string): boolean {
  if (!ISO_DATE.test(s)) return false;
  const d = new Date(`${s}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Given any ISO date, return the Monday of its week as a `YYYY-MM-DD` string. */
export function getMondayOf(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00Z`);
  const dow = d.getUTCDay(); // Sunday = 0, Monday = 1, … Saturday = 6
  const daysToMonday = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + daysToMonday);
  return d.toISOString().slice(0, 10);
}

/** Add `n` weeks (may be negative) to a Monday string, returning a Monday string. */
export function addWeeks(mondayStr: string, n: number): string {
  const d = new Date(`${mondayStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n * 7);
  return d.toISOString().slice(0, 10);
}

/** The Sunday that ends the week beginning on the given Monday. */
export function weekEndOf(mondayStr: string): string {
  const d = new Date(`${mondayStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6);
  return d.toISOString().slice(0, 10);
}

/**
 * Today's calendar date in Central time as `YYYY-MM-DD`.
 *
 * America/Chicago is the API's anchor for "today": week boundaries are computed
 * against it, and admin date validation rejects "in the future" against it. Any
 * client-side cap or default must therefore use the same anchor — otherwise a
 * viewer east of CT can pick a date the server then refuses.
 */
export function chicagoTodayIso(at: Date = new Date()): string {
  // en-CA renders as YYYY-MM-DD; America/Chicago matches the API's week anchor.
  // The instant is a parameter so a caller holding one can derive the matching date
  // from it rather than reading the clock a second time — two independent reads can
  // straddle midnight, which is precisely the boundary some callers exist to track.
  const parts = CHICAGO_DATE_FMT.formatToParts(at);
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
}

/** The current week's Monday, computed from today's calendar date in Central time. */
export function todayMondayIso(): string {
  return getMondayOf(chicagoTodayIso());
}

/**
 * Format a Monday–Sunday range as e.g. "May 18 – May 24, 2026". Parses at UTC
 * noon and formats in UTC (consistent with the rest of this module) so the
 * output never shifts with the runtime's local zone. The start year is shown
 * only when the two dates fall in different years (e.g. a week that straddles a
 * year boundary renders "Dec 28, 2026 – Jan 3, 2027").
 */
export function formatWeekRange(startDate: string, endDate: string): string {
  const fmt = (iso: string, withYear: boolean): string =>
    (withYear ? RANGE_FMT_WITH_YEAR : RANGE_FMT).format(new Date(`${iso}T12:00:00Z`));
  const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
  return `${fmt(startDate, !sameYear)} – ${fmt(endDate, true)}`;
}

/**
 * Whole calendar days between two `YYYY-MM-DD` dates, `today` minus `then`.
 *
 * `today` is a parameter, never read from the clock in here. That is the whole point:
 * two copies of this arithmetic existed, in `Approvals.tsx` and `AoboConfirmDialog`,
 * and both derived "today" from `new Date().toISOString()` — the UTC calendar day.
 * Between 19:00 and midnight Central (18:00 in winter) the UTC day is already tomorrow,
 * so every age read one day high and the approvals page classified sheets submitted
 * *yesterday* as having been stuck for two days. `Approvals.tsx` had a `todayIso()`
 * helper written specifically to avoid that, with a comment naming the exact window;
 * the helper simply was not used at those call sites.
 *
 * Taking the day as an argument makes that mistake unavailable rather than merely
 * discouraged, and lets callers be tested at a fixed instant. Anchored at UTC noon like
 * everything else here, so a DST transition inside the span cannot shift the count.
 *
 * BOTH ends have to be Chicago days, which the first version of this function got half
 * right: it took `today` as a parameter and then sliced `then` — a full `submittedAt`
 * timestamp — down to its UTC prefix. A sheet submitted at 19:30 Central carries a UTC
 * date of the NEXT day, so its age read one low and the two-day stuck threshold fired a
 * day late: the mirror of the bug this function was written to fix, inside the fix.
 *
 * `then` may therefore be either shape. A bare `YYYY-MM-DD` is already a calendar date
 * and is used as-is; anything longer is an instant and is resolved to the Chicago day it
 * falls on.
 */
export function daysBetweenIso(then: string, today: string): number {
  const thenDay = ISO_DATE.test(then) ? then : chicagoTodayIso(new Date(then));
  return Math.floor(
    (Date.parse(`${today}T12:00:00Z`) - Date.parse(`${thenDay}T12:00:00Z`)) / 86_400_000,
  );
}
